/**
 * TruHail Modern Theme - 2026 Urban Mobility Design
 * Dark-first, glassmorphism, vibrant gradients
 */

export const ModernColors = {
    // Primary palette
    primary: {
        main: '#00C2FF',
        light: '#33D4FF',
        dark: '#0099CC',
        gradient: 'linear-gradient(135deg, #00C2FF 0%, #8B5CF6 100%)',
    },
    secondary: {
        main: '#8B5CF6',
        light: '#A78BFA',
        dark: '#7C3AED',
    },

    // Semantic colors
    success: {
        main: '#10B981',
        light: '#34D399',
        dark: '#059669',
    },
    warning: {
        main: '#F59E0B',
        light: '#FBBF24',
        dark: '#D97706',
    },
    error: {
        main: '#EF4444',
        light: '#F87171',
        dark: '#DC2626',
    },

    // Dark theme backgrounds
    dark: {
        background: '#0A0A0F',
        surface: '#18181F',
        surfaceLight: '#1E1E2A',
        surfaceGlass: 'rgba(24, 24, 31, 0.8)',
        border: 'rgba(255, 255, 255, 0.08)',
        borderLight: 'rgba(255, 255, 255, 0.12)',
    },

    // Light theme backgrounds
    light: {
        background: '#FAFBFC',
        surface: '#FFFFFF',
        surfaceLight: '#F8FAFC',
        surfaceGlass: 'rgba(255, 255, 255, 0.85)',
        border: 'rgba(0, 0, 0, 0.06)',
        borderLight: 'rgba(0, 0, 0, 0.1)',
    },

    // Text colors
    text: {
        dark: {
            primary: '#F5F5F7',
            secondary: '#94A3B8',
            muted: '#64748B',
        },
        light: {
            primary: '#1A1A2E',
            secondary: '#64748B',
            muted: '#94A3B8',
        },
    },
};

export const ModernSpacing = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
};

export const ModernBorderRadius = {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    full: 9999,
};

export const ModernShadows = {
    sm: {
        shadowColor: '#00C2FF',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    md: {
        shadowColor: '#00C2FF',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 4,
    },
    lg: {
        shadowColor: '#00C2FF',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.2,
        shadowRadius: 16,
        elevation: 8,
    },
    glow: {
        shadowColor: '#00C2FF',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.4,
        shadowRadius: 20,
        elevation: 10,
    },
};

export const ModernTypography = {
    fontFamily: {
        regular: 'Inter-Regular',
        medium: 'Inter-Medium',
        semibold: 'Inter-SemiBold',
        bold: 'Inter-Bold',
    },
    sizes: {
        xs: 11,
        sm: 13,
        md: 15,
        lg: 17,
        xl: 20,
        xxl: 24,
        title: 28,
        hero: 36,
    },
};

// Glassmorphism styles for React Native
export const GlassMorphism = {
    card: {
        backgroundColor: ModernColors.dark.surfaceGlass,
        borderRadius: ModernBorderRadius.lg,
        borderWidth: 1,
        borderColor: ModernColors.dark.border,
        ...ModernShadows.md,
    },
    cardLight: {
        backgroundColor: ModernColors.light.surfaceGlass,
        borderRadius: ModernBorderRadius.lg,
        borderWidth: 1,
        borderColor: ModernColors.light.border,
        ...ModernShadows.md,
    },
};

// Common component styles
export const ModernComponents = {
    button: {
        primary: {
            backgroundColor: ModernColors.primary.main,
            borderRadius: ModernBorderRadius.md,
            paddingVertical: ModernSpacing.md,
            paddingHorizontal: ModernSpacing.lg,
            ...ModernShadows.glow,
        },
        secondary: {
            backgroundColor: 'transparent',
            borderRadius: ModernBorderRadius.md,
            borderWidth: 1,
            borderColor: ModernColors.primary.main,
            paddingVertical: ModernSpacing.md,
            paddingHorizontal: ModernSpacing.lg,
        },
    },
    input: {
        dark: {
            backgroundColor: ModernColors.dark.surface,
            borderRadius: ModernBorderRadius.md,
            borderWidth: 1,
            borderColor: ModernColors.dark.border,
            color: ModernColors.text.dark.primary,
            paddingHorizontal: ModernSpacing.md,
            paddingVertical: ModernSpacing.md,
        },
        light: {
            backgroundColor: ModernColors.light.surface,
            borderRadius: ModernBorderRadius.md,
            borderWidth: 1,
            borderColor: ModernColors.light.border,
            color: ModernColors.text.light.primary,
            paddingHorizontal: ModernSpacing.md,
            paddingVertical: ModernSpacing.md,
        },
    },
};

export default {
    colors: ModernColors,
    spacing: ModernSpacing,
    borderRadius: ModernBorderRadius,
    shadows: ModernShadows,
    typography: ModernTypography,
    glassMorphism: GlassMorphism,
    components: ModernComponents,
};
